import { listAllMedia } from "./media.js";
import { buildAiring } from "./airing.js";
import { listSubscriptions } from "./comic-subscriptions.js";
import { scheduleRows } from "../utils/comic-subscriptions.js";
import { buildPushNotices, normalizePushSettings } from "../utils/push-schedule.js";
import { todayDate } from "../utils/dates.js";

// Phone notifications (roadmap #61, #62): subscribe this device to Web Push and
// keep the Worker's copy of its next two weeks of release notices current.
// Device-local settings — each phone decides for itself.

const WORKER_URL = import.meta.env.VITE_WORKER_URL ?? "http://localhost:8787";
const ENABLED_KEY = "push:enabled";
const SETTINGS_KEY = "push:settings";
const LAST_SYNC_KEY = "push:lastSync";

export function pushSupported() {
	return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

// iOS only allows web push for an app added to the Home Screen.
export function needsInstallForPush() {
	const ios = /iPhone|iPad|iPod/.test(navigator.userAgent ?? "");
	const standalone = window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone === true;
	return ios && !standalone;
}

export function pushEnabled() {
	try {
		return localStorage.getItem(ENABLED_KEY) === "1";
	} catch {
		return false;
	}
}

export function loadPushSettings() {
	try {
		return normalizePushSettings(JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? "null"));
	} catch {
		return normalizePushSettings(null);
	}
}

export async function savePushSettings(settings) {
	try {
		localStorage.setItem(SETTINGS_KEY, JSON.stringify(normalizePushSettings(settings)));
	} catch {
		// not saved
	}
	if (pushEnabled()) await syncPushSchedule({ force: true }).catch(() => {});
}

function keyBytes(b64url) {
	const s = b64url.replace(/-/g, "+").replace(/_/g, "/");
	const bin = atob(s + "===".slice((s.length + 3) % 4));
	return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

async function post(path, body) {
	const resp = await fetch(`${WORKER_URL}${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
	const data = await resp.json().catch(() => ({}));
	if (!resp.ok) throw new Error(data.error ?? `Request failed: ${resp.status}`);
	return data;
}

async function currentSubscription() {
	const reg = await navigator.serviceWorker.ready;
	return reg.pushManager.getSubscription();
}

// Ask permission, subscribe, send the schedule. → number of notices scheduled.
export async function enablePush() {
	if (!pushSupported()) throw new Error("This browser can't receive notifications.");
	if (needsInstallForPush()) throw new Error("On iPhone and iPad, add MediaTracker to your Home Screen first (Share → Add to Home Screen), then turn this on from there.");
	const permission = await Notification.requestPermission();
	if (permission !== "granted") throw new Error("Notifications are blocked for this site — allow them in the browser's settings.");
	const keyResp = await fetch(`${WORKER_URL}/push/key`);
	const { key, ready } = await keyResp.json();
	if (!key || !ready) throw new Error("Notifications aren't set up on the server yet.");
	const reg = await navigator.serviceWorker.ready;
	const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) }));
	try {
		localStorage.setItem(ENABLED_KEY, "1");
	} catch {
		// fine
	}
	return syncPushSchedule({ force: true, subscription: sub });
}

export async function disablePush() {
	try {
		localStorage.removeItem(ENABLED_KEY);
	} catch {
		// fine
	}
	const sub = await currentSubscription().catch(() => null);
	if (!sub) return;
	await post("/push/unsubscribe", { subscription: sub.toJSON() }).catch(() => {});
	await sub.unsubscribe().catch(() => {});
}

export async function sendTestPush() {
	const sub = await currentSubscription();
	if (!sub) throw new Error("Turn notifications on first.");
	return post("/push/test", { subscription: sub.toJSON() });
}

// The daily hand-off (and after a settings change): what's coming, as notices.
export async function syncPushSchedule({ force = false, subscription = null } = {}) {
	if (!pushEnabled() || !pushSupported()) return null;
	const today = todayDate();
	try {
		if (!force && localStorage.getItem(LAST_SYNC_KEY) === today) return null;
	} catch {
		// sync anyway
	}
	const sub = subscription ?? (await currentSubscription());
	if (!sub) return null;
	const [items, airing, subs] = await Promise.all([
		listAllMedia(),
		buildAiring().catch(() => ({ upcomingEpisodes: [] })),
		listSubscriptions().catch(() => []),
	]);
	const notices = buildPushNotices({
		items,
		episodes: airing.upcomingEpisodes ?? [],
		comics: scheduleRows(subs),
		settings: loadPushSettings(),
		today,
	});
	const res = await post("/push/schedule", { subscription: sub.toJSON(), notices });
	try {
		localStorage.setItem(LAST_SYNC_KEY, today);
	} catch {
		// fine
	}
	return res.scheduled;
}
